import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react'
import { WidgetErrorBoundary } from './components/WidgetErrorBoundary'
import { WidgetFrame, WidgetSkeleton } from './components/WidgetFrame'
import type { WidgetRef } from './types'

export interface WidgetProps {
  widget: WidgetRef
}

// widget id -> component. The backend decides which ids a user gets (layout +
// permissions); this only maps an id to its UI. Each widget loads its own
// data, so adding one is: write the component, add a line here.
const WIDGETS: Record<string, LazyExoticComponent<ComponentType<WidgetProps>>> = {
  'my-day': lazy(() => import('./widgets/common/MyDayWidget')),
  // Development / Creative (Marketing resolves to the creative profile)
  'my-tasks': lazy(() => import('./widgets/dev/MyTasksWidget')),
  'task-kpis': lazy(() => import('./widgets/dev/TaskKpisWidget')),
  'tasks-by-space': lazy(() => import('./widgets/dev/TasksBySpaceWidget')),
  projects: lazy(() => import('./widgets/dev/ProjectsWidget')),
  'active-sprints': lazy(() => import('./widgets/dev/ActiveSprintsWidget')),
  'team-workload': lazy(() => import('./widgets/dev/TeamWorkloadWidget')),
  timesheets: lazy(() => import('./widgets/dev/TimesheetsWidget')),
  goals: lazy(() => import('./widgets/dev/GoalsWidget')),
  // Sales
  'my-alerts': lazy(() => import('./widgets/sales/MyAlertsWidget')),
  'my-activity': lazy(() => import('./widgets/sales/MyActivityWidget')),
  'my-followups': lazy(() => import('./widgets/sales/MyFollowupsWidget')),
  'my-collections': lazy(() => import('./widgets/sales/MyCollectionsWidget')),
  'my-dsr': lazy(() => import('./widgets/sales/MyDsrWidget')),
  'my-activity-stats': lazy(() => import('./widgets/sales/MyActivityStatsWidget')),
  'my-target': lazy(() => import('./widgets/sales/MyTargetWidget')),
  'sales-kpis': lazy(() => import('./widgets/sales/SalesKpisWidget')),
  'quotation-pipeline': lazy(() => import('./widgets/sales/QuotationPipelineWidget')),
  leads: lazy(() => import('./widgets/sales/LeadsWidget')),
  deals: lazy(() => import('./widgets/sales/DealsWidget')),
  'meetings-reminders': lazy(() => import('./widgets/sales/MeetingsRemindersWidget')),
  // Accounts
  'accounts-kpis': lazy(() => import('./widgets/accounts/AccountsKpisWidget')),
  'approvals-queue': lazy(() => import('./widgets/accounts/ApprovalsQueueWidget')),
  'milestones-due': lazy(() => import('./widgets/accounts/MilestonesDueWidget')),
  'recent-requests': lazy(() => import('./widgets/accounts/RecentRequestsWidget')),
  // HR
  'hr-kpis': lazy(() => import('./widgets/hr/HrKpisWidget')),
  'headcount-by-department': lazy(() => import('./widgets/hr/HeadcountByDepartmentWidget')),
  'attendance-today': lazy(() => import('./widgets/hr/AttendanceTodayWidget')),
  'pending-timesheets': lazy(() => import('./widgets/hr/PendingTimesheetsWidget')),
  'leave-requests': lazy(() => import('./widgets/hr/LeaveRequestsWidget')),
  // Company (superadmin overview)
  'company-headline': lazy(() => import('./widgets/company/CompanyHeadlineWidget')),
  'my-meetings-calendar': lazy(() => import('./widgets/meetings/MyMeetingsCalendarWidget')),
  'company-money': lazy(() => import('./widgets/company/CompanyMoneyWidget')),
  'company-clients': lazy(() => import('./widgets/company/CompanyClientsWidget')),
  'company-actions': lazy(() => import('./widgets/company/CompanyActionsWidget')),
  'company-sales': lazy(() => import('./widgets/company/CompanySalesWidget')),
  'company-scoreboard': lazy(() => import('./widgets/company/CompanyScoreboardWidget')),
  'company-lead-sources': lazy(() => import('./widgets/company/CompanyLeadSourcesWidget')),
  'company-forecast': lazy(() => import('./widgets/company/CompanyForecastWidget')),
  'company-collections': lazy(() => import('./widgets/company/CompanyCollectionsWidget')),
  'company-delivery': lazy(() => import('./widgets/company/CompanyDeliveryWidget')),
  'company-projects': lazy(() => import('./widgets/company/CompanyProjectsWidget')),
  'company-utilisation': lazy(() => import('./widgets/company/CompanyUtilisationWidget')),
  'company-servicedesk': lazy(() => import('./widgets/company/CompanyServiceDeskWidget')),
  'company-payroll': lazy(() => import('./widgets/company/CompanyPayrollWidget')),
  'company-hiring': lazy(() => import('./widgets/company/CompanyHiringWidget')),
  'company-joiners': lazy(() => import('./widgets/company/CompanyJoinersWidget')),
  'company-people': lazy(() => import('./widgets/company/CompanyPeopleWidget')),
}

export function isKnownWidget(id: string): boolean {
  return id in WIDGETS
}

/** Renders one widget, or a placeholder if this build doesn't know the id (e.g. backend deployed ahead of the app). */
export function WidgetRenderer({ widget }: WidgetProps) {
  const Component = WIDGETS[widget.id]
  if (!Component) {
    return (
      <WidgetFrame title={widget.title}>
        <p className="text-sm text-muted-foreground">This widget isn't available in this version of the app yet.</p>
      </WidgetFrame>
    )
  }
  return (
    <WidgetErrorBoundary title={widget.title}>
      <Suspense
        fallback={
          <WidgetFrame title={widget.title} isLoading>
            <WidgetSkeleton />
          </WidgetFrame>
        }
      >
        <Component widget={widget} />
      </Suspense>
    </WidgetErrorBoundary>
  )
}
